import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, TemplateRef, computed, viewChild } from "@angular/core";
import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { TemplateRendererComponent } from "@app/shared/components/tables/ag-grid-cells/template-renderer/template-renderer.component";
import { HearingPhaseBarDirective } from "../../hearing-detail/hearing-phase-bar.directive";
import { PostHearingActionRow } from "./post-hearing-workspace.models";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { createTerminologyGridHeader } from "@app/shared/terminology/terminology-grid-header";
import { POST_HEARING_ACTIONS_TERMINOLOGY } from "./post-hearing-actions.terminology";

@Component({
  selector: "phms-post-hearing-actions-section", standalone: true,
  imports: [CommonModule, UiLibraryModule, HearingPhaseBarDirective],
  templateUrl: "./post-hearing-actions-section.component.html",
  styles: [`:host { display: block; min-width: 0; } .row-action { display: flex; align-items: center; } .header-actions { display: flex; flex-wrap: wrap; gap: .5rem; }`],
})
export class PostHearingActionsSectionComponent {
  private rows: PostHearingActionRow[] = [];
  @Input() set actions(value: readonly PostHearingActionRow[]) { this.rows = [...value]; }
  get actions(): PostHearingActionRow[] { return this.rows; }
  @Input() busy = false;
  @Output() actionSelected = new EventEmitter<PostHearingActionRow>();
  readonly usage = POST_HEARING_ACTIONS_TERMINOLOGY;
  readonly text = createTerminologyUsageSignals(this.usage);
  readonly statusHeader = createTerminologyGridHeader(this.usage.status, this.text.status);
  private readonly statusCell = viewChild<TemplateRef<unknown>>("statusCell");
  private readonly actionCell = viewChild<TemplateRef<unknown>>("actionCell");
  readonly columnDefaults: ColDef<PostHearingActionRow> = {
    flex: 1, minWidth: 130, editable: false, sortable: false, filter: false, resizable: true,
    wrapText: true, autoHeight: true, wrapHeaderText: true, autoHeaderHeight: true,
    suppressKeyboardEvent: ({ event }) => event.key === "Tab",
    cellStyle: { whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: "1.4", fontWeight: "normal", paddingTop: "6px", paddingBottom: "6px" },
  };
  readonly gridOptions: GridOptions<PostHearingActionRow> = {
    pagination: false, defaultColDef: this.columnDefaults, getRowId: ({ data }) => data.id,
    suppressCellFocus: true, suppressColumnVirtualisation: true,
  };
  readonly columns = computed<ColDef<PostHearingActionRow>[]>(() => [
    { field: "label", headerName: "Action", flex: 1.4, minWidth: 210 },
    { field: "statusLabel", ...this.statusHeader(), cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.statusCell() } },
    { field: "owner", headerName: "Owner", minWidth: 160 },
    { field: "nextStep", headerName: "Next step", flex: 1.5, minWidth: 220 },
    { field: "updatedUtc", headerName: "Updated", minWidth: 160, valueFormatter: ({ value }) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(new Date(value)) : "\u2014" },
    { colId: "actions", headerName: "Actions", minWidth: 110, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.actionCell() } },
  ]);
  isPrimaryAction(action: PostHearingActionRow): boolean {
    return (action.id === "review" && action.actionLabel === "Complete") || (action.id === "approval" && action.actionLabel === "Approve");
  }
  select(action: PostHearingActionRow): void { if (!this.busy) this.actionSelected.emit(action); }
  revealColumn(event: FocusEvent, params: ICellRendererParams): void {
    if (event.target instanceof HTMLElement && event.target.matches(":focus-visible")) params.api.ensureColumnVisible(params.column!);
  }
}
